import OfferContext from './OfferContext';
import { useContext, useEffect, useReducer } from 'react';
import CurrencyContext from './CurrencyContext';
import ErrorMessageContext from './ErrorMessageContext';

// Constants
const DEFAULT_COUNTRY = "USA";
const DEFAULT_PROVIDER = "";
const DEFAULT_THEME = "simbud";
const DEFAULT_OFFERS = 6;
const PROVIDERS = ['Airalo', 'Nomad', 'RedTeaGo', 'Esimatic', 'Saily', 'Esim4travel', 'AlwaysMobile', 'Ubigi', 'Instabridge'];

// Helper functions
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    country: urlParams.get('country')?.toUpperCase() || DEFAULT_COUNTRY,
    referal: urlParams.get('referal')?.toLowerCase() || '',
    referalCode: urlParams.get('referalCode') || 'MARCOS4831',
    nberOffer: Number(urlParams.get('nberOffer')) || DEFAULT_OFFERS,
    provider: urlParams.get('provider') ? toTitleCase(urlParams.get('provider')) : DEFAULT_PROVIDER,
    theme: urlParams.get('theme')?.toLowerCase() || DEFAULT_THEME,
  };
};

const toTitleCase = (str) => {
  // Find the provider in PROVIDERS array that matches case-insensitively
  const provider = PROVIDERS.find(p => p.toLowerCase() === str.toLowerCase());
  return provider || str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getPlanUrl = (provider) => {
  const baseUrl = process.env.REACT_APP_PLAN_URL;
  let lowercaseProvider = provider.toLowerCase();
  
  return process.env.REACT_APP_ENV === 'development' 
    ? `${baseUrl}${lowercaseProvider}.json` 
    : `${baseUrl}${lowercaseProvider}`;
};

// Initial state
const initialOfferState = {
  data: [],
  loadedCountries: [],
  isLoaded: false,
  error: null,
  selectedCountries: [getUrlParams().country],
  selectedDefaultCountryName: undefined,
  selectedValidity: undefined,
  selectedCapacity: undefined,
  referal: getUrlParams().referal,
  referalCode: getUrlParams().referalCode,
  provider: getUrlParams().provider,
  nbreOffersDisplayed: getUrlParams().nberOffer,
  theme: getUrlParams().theme,
};

// Reducer
const offerReducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_COUNTRIES':
      return { ...state, selectedCountries: action.countries };
    case 'CHANGE_VALIDITY':
      return { ...state, selectedValidity: action.validity };
    case 'CHANGE_CAPACITY':
      return { ...state, selectedCapacity: action.capacity };
    case 'CHANGE_NBRE_OFFERS_DISPLAYED':
      return state.nbreOffersDisplayed >= 18 && action.nbreOffersDisplayed !== 3
        ? state
        : { ...state, nbreOffersDisplayed: action.nbreOffersDisplayed };
    case 'INIT':
      return {
        ...state,
        data: action.data,
        loadedCountries: action.loadedCountries,
        selectedDefaultCountryName: action.selectedDefaultCountryName,
        isLoaded: action.isLoaded
      };
    case 'ERROR':
      return { ...state, error: action.error, isLoaded: action.isLoaded };
    default:
      return state;
  }
};

const OfferProvider = (props) => {
  const currencyCtx = useContext(CurrencyContext);
  const errorMessageCtx = useContext(ErrorMessageContext);
  const [offerState, dispatchOfferAction] = useReducer(offerReducer, initialOfferState);

  // Action creators
  const changeCountriesToOfferHandler = (countries) => {
    dispatchOfferAction({ type: 'CHANGE_COUNTRIES', countries });
  };

  const changeValiditytoOfferHandler = (validity) => {
    dispatchOfferAction({ type: 'CHANGE_VALIDITY', validity });
  };

  const changeCapacitytoOfferHandler = (capacity) => {
    dispatchOfferAction({ type: 'CHANGE_CAPACITY', capacity });
  };

  const changeNberOfferstoOfferHandler = (nbreOffers) => {
    dispatchOfferAction({ type: 'CHANGE_NBRE_OFFERS_DISPLAYED', nbreOffersDisplayed: nbreOffers });
  };

  const initiateDataHandler = (data, loadedCountries, selectedDefaultCountryName, isLoaded) => {
    dispatchOfferAction({ type: 'INIT', data, loadedCountries, selectedDefaultCountryName, isLoaded });
  };

  const setErrorHandler = (isLoaded, error) => {
    dispatchOfferAction({ type: 'ERROR', isLoaded, error });
  };

  // Context value
  const offerContext = {
    ...offerState,
    changeCountries: changeCountriesToOfferHandler,
    changeValidity: changeValiditytoOfferHandler,
    changeCapacity: changeCapacitytoOfferHandler,
    changeNberOffers: changeNberOfferstoOfferHandler
  };

  // Data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchOptions = {
          headers: { 'Accept': 'application/json' },
          redirect: 'follow',
        };

        const responses = await Promise.all([
          fetch(process.env.REACT_APP_COUNTRY_URL, fetchOptions),
          ...PROVIDERS.map(provider => fetch(getPlanUrl(provider), fetchOptions))
        ]);

        responses.forEach(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.url}, status: ${response.status}`);
          }
        });

        const dataJSON = await Promise.all(responses.map(response => response.json()));

        const loadedCountries = Object.entries(dataJSON[0]).map(([key, value]) => ({
          id: key,
          name: value.name.common,
          nameFrench: value.translations.fra,
          code: value.cca3
        })).sort((a, b) => a.name.localeCompare(b.name));

        let selectedDefaultCountryName = loadedCountries.find(
          item => item.code === offerState.selectedCountries[0]
        );

        if (!selectedDefaultCountryName) {
          selectedDefaultCountryName = loadedCountries.find(item => item.code === DEFAULT_COUNTRY);
        }

        const selectedDefaultCountryNameFrench = {
          labelFrench: selectedDefaultCountryName.nameFrench.common,
          labelEnglish: selectedDefaultCountryName.name,
          value: selectedDefaultCountryName.code,
        };
        
        const providerNotRecognized = !PROVIDERS.some(p => p.toLowerCase() === offerState.provider.toLowerCase()) && offerState.provider !== "";

        if (providerNotRecognized) {
          const errorMessage = currencyCtx.languageParentWindow === 'fr'
            ? `"${offerState.provider}" non reconnu pour data-provider, remplacé par "${DEFAULT_PROVIDER}"`
            : `"${offerState.provider}" not recognized for data-provider, replaced with "${DEFAULT_PROVIDER}"`;
          errorMessageCtx.addErrorMessage(errorMessage);
        }

        const data = dataJSON.slice(1).flat();

        initiateDataHandler(data, loadedCountries, selectedDefaultCountryNameFrench, true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorHandler(true, error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCtx.languageParentWindow]);

  return (
    <OfferContext.Provider value={offerContext}>
      {props.children}
    </OfferContext.Provider>
  );
};

export default OfferProvider;